import { useRef, useState, useEffect } from "react";
import { SetRow } from "@/hooks/useProducts";
import FeaturedCarouselCard from "./FeaturedCarouselCard";
import { Pause, Play } from "lucide-react";

interface FeaturedProductsMobileProps {
  sets: SetRow[];
}

export const FeaturedProductsMobile = ({ sets }: FeaturedProductsMobileProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);

  // Duplicamos 3 veces para permitir scroll horizontal prolongado
  const duplicatedSets = [...sets, ...sets, ...sets];

  // Auto-scroll muy pausado frame a frame mediante requestAnimationFrame
  // Se pausa automáticamente si el usuario toca o interactúa
  useEffect(() => {
    let animationFrameId: number;
    let lastTime = performance.now();

    const step = (time: number) => {
      const delta = time - lastTime;
      lastTime = time;

      if (!isPaused && !isInteracting && scrollRef.current) {
        // Velocidad suave: ~25 píxeles por segundo
        const scrollSpeed = 0.035 * delta;
        scrollRef.current.scrollLeft += scrollSpeed;

        // Si llega cerca de 2/3 del contenido, volvemos sutilmente a 1/3 para el loop
        const maxScroll = scrollRef.current.scrollWidth / 2;
        if (scrollRef.current.scrollLeft >= maxScroll) {
          scrollRef.current.scrollLeft -= maxScroll / 2;
        }
      }

      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPaused, isInteracting]);

  return (
    <div className="relative">
      {/* Indicador superior con badge y botón de pausa */}
      <div className="flex items-center justify-between mb-3 px-1">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground font-medium bg-muted/60 px-2 py-0.5 rounded-full border border-border/50">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Disponibles ahora
        </span>

        <button
          onClick={() => setIsPaused((prev) => !prev)}
          aria-label={isPaused ? "Reanudar carrusel" : "Pausar carrusel"}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted/40 border border-border/40"
        >
          {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          <span className="text-[10px]">{isPaused ? "Reanudar" : "Pausar"}</span>
        </button>
      </div>

      {/* Contenedor móvil con soporte táctil, snap-scroll opcional y difuminados laterales */}
      <div className="relative w-full overflow-hidden rounded-xl">
        {/* Máscara izquierda difuminada */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-r from-background to-transparent" />

        <div
          ref={scrollRef}
          onTouchStart={() => setIsInteracting(true)}
          onTouchEnd={() => {
            // Reanudar tras 2.5s sin tocar
            setTimeout(() => setIsInteracting(false), 2500);
          }}
          className="flex gap-4 overflow-x-auto no-scrollbar py-2 px-4 touch-pan-x"
          style={{
            WebkitOverflowScrolling: "touch",
          }}
        >
          {duplicatedSets.map((set, index) => (
            <div
              key={`mob-${set.id}-${index}`}
              className="flex-shrink-0 w-[240px]"
            >
              <FeaturedCarouselCard set={set} />
            </div>
          ))}
        </div>

        {/* Máscara derecha difuminada */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-l from-background to-transparent" />
      </div>
    </div>
  );
};

export default FeaturedProductsMobile;
